/**
 * Developer-facing landing page for token-kit.
 */

import React from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import {
  ArrowRight,
  Code2,
  Zap,
  Shield,
  BarChart3,
  Webhook,
  Globe,
  CreditCard,
  Users,
  DollarSign,
  Terminal,
  CheckCircle2,
  Sparkles,
  Gamepad2,
  ListChecks,
  Briefcase,
  GraduationCap,
} from 'lucide-react';

// export const TIERS = [
//   { name: 'Free', price: '$0', period: '/mo', rpm: '500 req/min', promoNote: '', features: ['Full SDK access', 'Community support', 'Developer dashboard', '3 API keys'] },
//   { name: 'Pro', price: '$25', period: '/mo', rpm: '2,000 req/min', promoNote: 'Promo: first 10 months, then $49/mo', popular: true, features: ['Priority support (24h SLA)', 'Usage analytics', 'Webhooks (coming soon)', '10 API keys'] },
//   { name: 'Enterprise', price: 'Custom', period: '', rpm: 'Custom', promoNote: '', features: ['SLA guarantee', 'Custom model routing', 'Dedicated support', 'White-label options', 'Unlimited keys'] },
// ];

export const TIERS = [
  { name: 'Beta period', price: '$0', period: '/mo', rpm: '30 req/min', promoNote: '', features: ['Full SDK access', 'Community support', 'Developer dashboard', '2 API keys'], popular: false }
]

export default function LandingPage() {
  return (
    <div className="landing">
      <Navbar />

      {/* ── Beta notice ────────────────────────────────────── */}
      <div className="beta-banner">
        token-kit is in <strong>beta</strong> — some features are under active development.
      </div>

      {/* ── Hero ──────────────────────────────────────────── */}
      <section className="hero">
        <div className="hero-glow" />
        <div className="container hero-content">
          <div className="hero-badge">
            <Sparkles size={14} />
            <span>AI Billing + Metering SDK</span>
          </div>
          <h1>
            Add AI to Your App.
            <br />
            <span className="gradient-text">Users Pay Per Use.</span>
          </h1>
          <p className="hero-sub">
            Built for indie developers and lean product teams. Integrate Claude, GPT-4o,
            and Amazon Nova with prepaid user credits, spend caps, cost estimation, and
            provider routing in one SDK.
          </p>
          <div className="hero-actions">
            <Link to="/register" className="btn btn-accent btn-lg">
              Request Access <ArrowRight size={18} />
            </Link>
            <a href="#docs" className="btn btn-ghost btn-lg">
              Quick Start
            </a>
            <a href="https://www.npmjs.com/package/@codetunezstudios/token-kit" className="btn btn-ghost btn-lg">
              NPMJS
            </a>
          </div>
          <div className="hero-code">
            <div className="code-header">
              <Terminal size={14} /> Quick Start
            </div>
            <pre><code>{`import TokenKit from '@codetunezstudios/token-kit';

const tk = new TokenKit({ apiKey: process.env.TOKENKIT_API_KEY });

const response = await tk.chat(
  'ut_user123_abc456',
  [{ role: 'user', content: 'Hello, AI!' }],
  { model: 'gpt-4o' }
);
// That's it — tokens deducted from user's balance`}</code></pre>
          </div>
        </div>
      </section>

      {/* ── How It Works ─────────────────────────────────── */}
      <section id="how-it-works" className="section section-howit">
        <div className="container">
          <div className="section-header">
            <h2>How It Works</h2>
            <p>Three steps to AI features with no running LLM costs to you</p>
          </div>
          <div className="steps-grid">
            <div className="step-card">
              <div className="step-number">1</div>
              <div className="step-icon"><Code2 size={32} /></div>
              <h3>Register and integrate</h3>
              <p>Create your developer account, get your API key, and install the token-kit SDK to connect AI usage in your app.</p>
            </div>
            <div className="step-card">
              <div className="step-number">2</div>
              <div className="step-icon"><Users size={32} /></div>
              <h3>Users get tokens</h3>
              <p>Users create an account on <a href="https://ai-tokens.me" target="_blank" rel="noopener noreferrer">ai-tokens.me</a> and top up with token packages which we use to pay for LLM requests made by your application.</p>
            </div>
            <div className="step-card">
              <div className="step-number">3</div>
              <div className="step-icon"><DollarSign size={32} /></div>
              <h3>AI features unlock in your app</h3>
              <p>Your app includes AI features that unlock when a user has enough balance. token-kit handles billing, token tracking, and access to the AI models behind the scenes.</p>
            </div>
          </div>
        </div>
      </section>

      {/* ── Use Cases ─────────────────────────────────── */}
      <section className="section section-usecases">
        <div className="container">
          <div className="section-header">
            <h2>What Will You Build?</h2>
            <p>See how developers add AI features to real apps — in minutes</p>
          </div>
          <div className="usecases-grid">

            {/* Game App */}
            <div className="usecase-card">
              <div className="usecase-icon"><Gamepad2 size={28} /></div>
              <h3>Game: AI Dungeon Master</h3>
              <p className="usecase-desc">
                A fantasy RPG generates unique quests, NPC dialogue, and story branches on the fly.
                Players top up tokens to unlock richer storylines — you never pay for the AI.
              </p>
              <div className="usecase-code">
                <div className="code-header"><Terminal size={14} /> quest-generator.ts</div>
                <pre><code>{`const quest = await tk.chat(userToken, [{
    role: 'system',
    content: 'You are a fantasy quest master.'
  }, {
    role: 'user',
    content: \`The player is a level \${player.level}
warrior in \${player.location}. Generate a
unique side quest with rewards.\`
  }], { model: 'gpt-4o' });

showQuestDialog(quest.message.content);`}</code></pre>
              </div>
              <div className="usecase-tag">~200 tokens per quest</div>
            </div>

            {/* Productivity App */}
            <div className="usecase-card">
              <div className="usecase-icon"><ListChecks size={28} /></div>
              <h3>Productivity: Smart To-Do</h3>
              <p className="usecase-desc">
                A task manager that breaks vague goals into actionable steps, estimates time,
                and suggests priorities. Users pay per AI assist — you keep building.
              </p>
              <div className="usecase-code">
                <div className="code-header"><Terminal size={14} /> task-breakdown.ts</div>
                <pre><code>{`const plan = await tk.chat(userToken, [{
    role: 'system',
    content: 'Break tasks into actionable steps.'
  }, {
    role: 'user',
    content: \`Break this goal into subtasks with
time estimates: "\${task.title}"\`
  }], { model: 'gpt-4o' });

addSubtasks(JSON.parse(plan.message.content));`}</code></pre>
              </div>
              <div className="usecase-tag">~150 tokens per breakdown</div>
            </div>

            {/* Business App */}
            <div className="usecase-card">
              <div className="usecase-icon"><Briefcase size={28} /></div>
              <h3>Business: Email Draft Assistant</h3>
              <p className="usecase-desc">
                A CRM that drafts professional follow-up emails from meeting notes.
                Sales reps top up tokens for AI-written outreach — zero LLM cost for you.
              </p>
              <div className="usecase-code">
                <div className="code-header"><Terminal size={14} /> email-drafter.ts</div>
                <pre><code>{`const draft = await tk.chat(userToken, [{
    role: 'system',
    content: 'You write concise sales emails.'
  }, {
    role: 'user',
    content: \`Draft a follow-up email to
\${contact.name} at \${contact.company}.
Meeting notes: \${meeting.summary}\`
  }], { model: 'gpt-4o' });

composeEmail(contact.email, draft.message.content);`}</code></pre>
              </div>
              <div className="usecase-tag">~300 tokens per draft</div>
            </div>

            {/* Education App */}
            <div className="usecase-card">
              <div className="usecase-icon"><GraduationCap size={28} /></div>
              <h3>Education: AI Study Coach</h3>
              <p className="usecase-desc">
                A learning app that explains complex topics at the student's level,
                generates practice questions, and gives feedback on answers.
              </p>
              <div className="usecase-code">
                <div className="code-header"><Terminal size={14} /> study-coach.ts</div>
                <pre><code>{`const explanation = await tk.chat(userToken, [{
    role: 'system',
    content: \`Explain concepts clearly for a \${student.gradeLevel} student.\`
  }, {
    role: 'user',
    content: \`Explain \${topic.name} in simple
terms, then give me 3 practice questions.\`
  }], { model: 'gpt-4o' });

renderLesson(explanation.message.content);`}</code></pre>
              </div>
              <div className="usecase-tag">~250 tokens per lesson</div>
            </div>

          </div>
        </div>
      </section>

      {/* ── Features ─────────────────────────────────────── */}
      <section id="features" className="section section-features">
        <div className="container">
          <div className="section-header">
            <h2>Built for Developers</h2>
            <p>Everything you need to ship AI features fast</p>
          </div>
          <div className="features-grid">
            <div className="feature-card">
              <Zap size={24} />
              <h3>Multi-Model Support</h3>
              <p>Claude, GPT-4o, Amazon Nova — all through one unified API. Switch models without changing code.</p>
            </div>
            <div className="feature-card">
              <Shield size={24} />
              <h3>Security First</h3>
              <p>SHA-256 hashed API keys, rate limiting, and full audit trails. Enterprise-grade security by default.</p>
            </div>
            <div className="feature-card">
              <BarChart3 size={24} />
              <h3>Usage Analytics</h3>
              <p>Track requests, token consumption, and user activity from your developer dashboard. Monitor usage and performance.</p>
            </div>
            <div className="feature-card">
              <Webhook size={24} />
              <h3>Webhooks <span style={{ fontSize: '0.7rem', opacity: 0.7 }}>(Coming Soon)</span></h3>
              <p>Get notified when users run low on tokens, make purchases, or hit spending limits. Drive engagement automatically.</p>
            </div>
            <div className="feature-card">
              <Globe size={24} />
              <h3>TypeScript SDK</h3>
              <p>TypeScript-first SDK works with Node.js and modern browsers via bundlers. Full type safety included.</p>
            </div>
            <div className="feature-card">
              <CreditCard size={24} />
              <h3>Zero LLM Costs</h3>
              <p>Users pay for their own token usage. You never see a surprise LLM bill — even if your app goes viral.</p>
            </div>
          </div>
        </div>
      </section>

      {/* ── Pricing ──────────────────────────────────────── */}
      <section id="pricing" className="section section-pricing">
        <div className="container">
          <div className="section-header">
            <h2>Developer Pricing</h2>
            <p>Start free. Scale as you grow.</p>
          </div>
          <div className="pricing-grid">
            {TIERS.map((t) => (
              <div key={t.name} className={`pricing-card ${t.popular ? 'popular' : ''}`}>
                {t.popular && <div className="popular-badge">Most Popular</div>}
                <h3>{t.name}</h3>
                <div className="pricing-price">
                  {t.price}<span className="pricing-period">{t.period}</span>
                </div>
                {t.promoNote && <div className="pricing-meta">{t.promoNote}</div>}
                <div className="pricing-meta">{t.rpm}</div>
                <ul className="pricing-features">
                  {t.features.map((f) => (
                    <li key={f}><CheckCircle2 size={14} /> {f}</li>
                  ))}
                </ul>
                <Link
                  to="/register"
                  className={`btn ${t.popular ? 'btn-accent' : 'btn-outline'} btn-block`}
                >
                  {t.name === 'Enterprise' ? 'Contact Sales' : 'Get Started'}
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Docs snippet ─────────────────────────────────── */}
      <section id="docs" className="section section-docs">
        <div className="container">
          <div className="section-header">
            <h2>Quick Start</h2>
            <p>From zero to AI in under 5 minutes</p>
          </div>
          <div className="docs-steps">
            <div className="docs-step">
              <span className="docs-step-num">01</span>
              <div>
                <h3>Install</h3>
                <div className="code-block"><code>npm install @codetunezstudios/token-kit</code></div>
              </div>
            </div>
            <div className="docs-step">
              <span className="docs-step-num">02</span>
              <div>
                <h3>Initialize</h3>
                <div className="code-block">
                  <code>{`const tk = new TokenKit({ apiKey: 'dev_your_key' });`}</code>
                </div>
              </div>
            </div>
            <div className="docs-step">
              <span className="docs-step-num">03</span>
              <div>
                <h3>Make Requests</h3>
                <div className="code-block">
                  <code>{`const res = await tk.chat(userToken, [{ role: 'user', content: prompt }], { model: 'gpt-4o' });`}</code>
                </div>
              </div>
            </div>
            <div className="docs-step">
              <span className="docs-step-num">04</span>
              <div>
                <h3>Ship It</h3>
                <p className="docs-step-desc">Tokens deducted automatically. Usage tracked in real time. Focus on building.</p>
              </div>
            </div>
          </div>
          <p className="docs-cta">
            Want the full API reference, examples, and guides? <Link to="/sdk">Explore the SDK Docs →</Link>
          </p>
        </div>
      </section>
      <section className="section cta-section">
        <div className="container container-sm" style={{ textAlign: 'center' }}>
          <h2>Ready to Add AI to Your App?</h2>
          <p>Request beta access and launch pay-per-use AI features without building billing infrastructure from scratch.</p>
          <Link to="/register" className="btn btn-accent btn-lg">
            Request Access <ArrowRight size={18} />
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
